import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Sync as SyncIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { authGet, authPut, authPost, authDelete } from '../../services/apiService';
import { format } from 'date-fns';

const darkGreenColor = "#1D4434";

/**
 * AccessibilityAdmin component for managing accessibility configuration and syncing
 */
const AccessibilityAdmin = () => {
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [resettingAll, setResettingAll] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [configSuccess, setConfigSuccess] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  
  const [adoQueryUrl, setAdoQueryUrl] = useState(''); // Legacy
  const [wcagQueries, setWcagQueries] = useState({
    '2.0': '',
    '2.1': '',
    '2.2': ''
  });
  const [lastSync, setLastSync] = useState(null);
  const [syncIntervalHours, setSyncIntervalHours] = useState(4);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoadingConfig(true);
      const response = await authGet('/api/admin/accessibility/config');
      
      if (response.success) {
        setAdoQueryUrl(response.config.adoQueryUrl || ''); // Legacy
        setWcagQueries(response.config.wcagQueries || {
          '2.0': '',
          '2.1': '',
          '2.2': ''
        });
        setLastSync(response.config.lastSync || null);
        setSyncIntervalHours(response.config.syncIntervalHours || 4);
      } else {
        setConfigError('Failed to load configuration');
      }
    } catch (error) {
      console.error('Error loading accessibility config:', error);
      setConfigError(error.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true);
      setConfigError(null);
      setConfigSuccess(null);
      
      const response = await authPut('/api/admin/accessibility/config', {
        wcagQueries: {
          '2.0': (wcagQueries['2.0'] || '').trim(),
          '2.1': (wcagQueries['2.1'] || '').trim(),
          '2.2': (wcagQueries['2.2'] || '').trim()
        },
        syncIntervalHours: parseInt(syncIntervalHours)
      });
      
      if (response.success) {
        setConfigSuccess('Configuration saved successfully');
        setLastSync(response.config.lastSync || null);
        setTimeout(() => setConfigSuccess(null), 3000);
      } else {
        setConfigError(response.message || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving accessibility config:', error);
      setConfigError(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      setConfigError(null);
      
      const response = await authPost('/api/admin/accessibility/sync');
      
      if (response.success) {
        setSyncResult({
          success: true,
          message: response.message,
          processed: response.processed,
          created: response.created,
          updated: response.updated,
          errors: response.errors
        });
        // Reload config to get updated lastSync
        await loadConfig();
      } else {
        setSyncResult({
          success: false,
          message: response.message || 'Sync failed'
        });
      }
    } catch (error) {
      console.error('Error syncing accessibility issues:', error);
      setSyncResult({
        success: false,
        message: error.response?.data?.message || 'Failed to sync accessibility issues'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleResetAll = async () => {
    try {
      setResettingAll(true);
      setConfigError(null);
      setConfigSuccess(null);
      
      const response = await authDelete('/api/admin/accessibility/data');
      
      if (response.success) {
        setConfigSuccess(`Successfully reset all accessibility data. Deleted ${response.deletedCount || 0} issue(s) and reset sync date.`);
        setLastSync(null);
        setTimeout(() => setConfigSuccess(null), 5000);
        setResetConfirmOpen(false);
      } else {
        setConfigError(response.message || 'Failed to reset accessibility data');
        setResetConfirmOpen(false);
      }
    } catch (error) {
      console.error('Error resetting accessibility data:', error);
      setConfigError(error.response?.data?.message || 'Failed to reset accessibility data');
      setResetConfirmOpen(false);
    } finally {
      setResettingAll(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: darkGreenColor }}>
        Accessibility Configuration
      </Typography>

      {/* Messages */}
      {configSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setConfigSuccess(null)}>
          {configSuccess}
        </Alert>
      )}
      {configError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setConfigError(null)}>
          {configError}
        </Alert>
      )}
      {syncResult && (
        <Alert 
          severity={syncResult.success ? 'success' : 'error'} 
          sx={{ mb: 2 }} 
          onClose={() => setSyncResult(null)}
        >
          {syncResult.success ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {syncResult.message}
              </Typography>
              {syncResult.processed !== undefined && (
                <Box>
                  <Typography variant="body2">
                    Processed: {syncResult.processed} | Created: {syncResult.created} | Updated: {syncResult.updated}
                  </Typography>
                  {syncResult.wcagResults && Object.keys(syncResult.wcagResults).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        By WCAG Version:
                      </Typography>
                      {Object.entries(syncResult.wcagResults).map(([version, stats]) => (
                        <Typography key={version} variant="body2" sx={{ pl: 2 }}>
                          WCAG {version}: {stats.processed} processed ({stats.created} created, {stats.updated} updated)
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              {syncResult.errors && syncResult.errors.length > 0 && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                  {syncResult.errors.length} error(s) occurred during sync
                </Typography>
              )}
            </Box>
          ) : (
            syncResult.message
          )}
        </Alert>
      )}

      {/* Configuration Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SettingsIcon sx={{ mr: 1, color: darkGreenColor }} />
          <Typography variant="h6" sx={{ color: darkGreenColor }}>
            ADO Query Configuration
          </Typography>
        </Box>

        {loadingConfig ? (
          <LinearProgress sx={{ mb: 2 }} />
        ) : (
          <Grid container spacing={3}>
            {/* WCAG 2.0 Query */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: darkGreenColor }}>
                WCAG 2.0 Query
              </Typography>
              <TextField
                label="ADO Query URL or WIQL Query for WCAG 2.0"
                value={wcagQueries['2.0'] || ''}
                onChange={(e) => setWcagQueries({ ...wcagQueries, '2.0': e.target.value })}
                placeholder="https://dev.azure.com/{org}/{project}/_queries/query/{queryId} or WIQL query"
                fullWidth
                multiline
                rows={3}
                disabled={savingConfig}
                helperText="Enter the ADO query URL or WIQL query to fetch WCAG 2.0 accessibility issues"
              />
            </Grid>
            
            {/* WCAG 2.1 Query */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: darkGreenColor }}>
                WCAG 2.1 Query
              </Typography>
              <TextField
                label="ADO Query URL or WIQL Query for WCAG 2.1"
                value={wcagQueries['2.1'] || ''}
                onChange={(e) => setWcagQueries({ ...wcagQueries, '2.1': e.target.value })}
                placeholder="https://dev.azure.com/{org}/{project}/_queries/query/{queryId} or WIQL query"
                fullWidth
                multiline
                rows={3}
                disabled={savingConfig}
                helperText="Enter the ADO query URL or WIQL query to fetch WCAG 2.1 accessibility issues"
              />
            </Grid>
            
            {/* WCAG 2.2 Query */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: darkGreenColor }}>
                WCAG 2.2 Query
              </Typography>
              <TextField
                label="ADO Query URL or WIQL Query for WCAG 2.2"
                value={wcagQueries['2.2'] || ''}
                onChange={(e) => setWcagQueries({ ...wcagQueries, '2.2': e.target.value })}
                placeholder="https://dev.azure.com/{org}/{project}/_queries/query/{queryId} or WIQL query"
                fullWidth
                multiline
                rows={3}
                disabled={savingConfig}
                helperText="Enter the ADO query URL or WIQL query to fetch WCAG 2.2 accessibility issues"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sync Interval (Hours)"
                type="number"
                value={syncIntervalHours}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 1) {
                    setSyncIntervalHours(value);
                  }
                }}
                fullWidth
                disabled={savingConfig}
                inputProps={{ min: 1, step: 1 }}
                helperText="Number of hours between automatic syncs (default: 4 hours)"
              />
            </Grid>
            
            {lastSync && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                  Last synced: {format(new Date(lastSync), 'PPpp')}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleSaveConfig}
                disabled={savingConfig || loadingConfig}
                startIcon={savingConfig ? <LinearProgress size={20} /> : <SaveIcon />}
                sx={{
                  backgroundColor: darkGreenColor,
                  '&:hover': { backgroundColor: '#0f2e1f' }
                }}
              >
                {savingConfig ? 'Saving...' : 'Save WCAG Query URLs'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleSync}
                disabled={syncing || loadingConfig || (!(wcagQueries['2.0'] || '').trim() && !(wcagQueries['2.1'] || '').trim() && !(wcagQueries['2.2'] || '').trim())}
                startIcon={syncing ? <LinearProgress size={20} /> : <SyncIcon />}
                sx={{
                  borderColor: darkGreenColor,
                  color: darkGreenColor,
                  '&:hover': { borderColor: '#0f2e1f', backgroundColor: 'rgba(29, 68, 52, 0.04)' }
                }}
              >
                {syncing ? 'Syncing...' : 'Sync All WCAG Issues'}
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                onClick={() => setResetConfirmOpen(true)}
                disabled={resettingAll || loadingConfig}
                startIcon={<DeleteIcon />}
                sx={{
                  borderColor: '#d32f2f',
                  color: '#d32f2f',
                  '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                }}
              >
                Reset All
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Information Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ color: darkGreenColor }}>
            How to configure ADO Query URL
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            You can provide either:
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2 }}>
            <ul>
              <li>
                <strong>ADO Query URL:</strong> The URL of a saved query in Azure DevOps.
                <br />
                Format: <code>https://dev.azure.com/&#123;org&#125;/&#123;project&#125;/_queries/query/&#123;queryId&#125;</code>
              </li>
              <li>
                <strong>WIQL Query:</strong> A direct WIQL (Work Item Query Language) query.
                <br />
                Example: <code>SELECT [System.Id] FROM WorkItems WHERE [System.Tags] CONTAINS 'Accessibility'</code>
              </li>
            </ul>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The system will automatically extract the WIQL query from the URL if you provide a saved query URL.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Reset All Confirmation Dialog */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => !resettingAll && setResetConfirmOpen(false)}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title" sx={{ color: '#d32f2f' }}>
          Reset All Accessibility Data
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            Are you sure you want to reset all accessibility data? This will:
            <br /><br />
            <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>
              <li>Delete all accessibility issues from the database</li>
              <li>Reset the sync date</li>
            </ul>
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResetConfirmOpen(false)} 
            disabled={resettingAll}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResetAll} 
            color="error" 
            variant="contained"
            disabled={resettingAll}
            startIcon={resettingAll ? <LinearProgress size={20} /> : <DeleteIcon />}
          >
            {resettingAll ? 'Resetting...' : 'Reset All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessibilityAdmin;

